import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api, saveAuthData } from '@/utils/api';
import { setCookie, parseCookies } from 'nookies';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { parseDiscordReturnState, userDataFromAuthLoginBody } from '@/utils/login-response';
import type { User } from '@/context/AuthContext';

const getApiBody = (response: any) => response?.data ?? response;

const extractToken = (payload: any) =>
    payload?.token ??
    payload?.access_token ??
    payload?.data?.token ??
    payload?.data?.access_token;

const hasActiveVip = (userData: any) =>
    !!(userData?.plan?.isActive || userData?.is_vip || userData?.vip_acaba_em || userData?.vip_acabaem);

export default function EntrarDiscordPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login: authLogin } = useAuth();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState('Conectando com Discord...');

    useEffect(() => {
        const code = searchParams.get('code');
        const state = searchParams.get('state');

        if (!code) {
            setStatus('error');
            setMessage('Código de autorização não encontrado.');
            return;
        }

        const parsedState = parseDiscordReturnState(state);
        if (parsedState.mode === 'app') {
            const target = `mediocre://discord/oauth2?code=${encodeURIComponent(code)}${state ? `&state=${encodeURIComponent(state)}` : ''}`;
            setMessage('Abrindo o aplicativo...');
            window.location.href = target;
            const timeout = setTimeout(() => {
                setStatus('error');
                setMessage('Não foi possível abrir o aplicativo automaticamente.');
            }, 1500);
            return () => clearTimeout(timeout);
        }

        const postLoginNavigate = (userData: unknown) => {
            const hasVip = hasActiveVip(userData);
            const fallback = hasVip ? '/' : '/landing';
            const next =
                parsedState.mode === 'web' && parsedState.next?.startsWith('/')
                    ? parsedState.next
                    : fallback;
            setTimeout(() => navigate(next, { replace: true }), 1000);
        };

        const processLogin = async () => {
            try {
                setMessage('Autenticando...');
                const redirectUri = window.location.origin + '/entrar-discord';
                const cookies = parseCookies();
                const currentToken = cookies['token'];

                if (currentToken) {
                    setMessage('Vinculando conta do Discord...');
                    await api.post('usuarios/me/social/discord', { code, redirectUri });
                    setStatus('success');
                    setMessage('Conta vinculada com sucesso!');
                    const userResponse = await api.get('usuarios/me');
                    const userData = getApiBody(userResponse) as User;
                    saveAuthData(currentToken, userData);
                    authLogin(currentToken, userData);
                    postLoginNavigate(userData);
                    return;
                }

                const loginResponse = await api.post('auth/login/discord', { code, redirectUri });
                const loginData = getApiBody(loginResponse) as Record<string, unknown>;
                const token = extractToken(loginData);

                if (token) {
                    setStatus('success');
                    setMessage('Login realizado com sucesso!');
                    setCookie(null, 'token', token, { maxAge: 60 * 60 * 24 * 7, path: '/' });
                    const fromBody = userDataFromAuthLoginBody(loginData);
                    let userData: User;
                    if (fromBody && typeof fromBody.usr_id === 'number') {
                        try {
                            const userResponse = await api.get('usuarios/me', {
                                headers: { Authorization: `Bearer ${token}` },
                            });
                            userData = getApiBody(userResponse) as User;
                        } catch {
                            userData = fromBody as User;
                        }
                    } else {
                        const userResponse = await api.get('usuarios/me', {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        userData = getApiBody(userResponse) as User;
                    }
                    saveAuthData(token, userData);
                    authLogin(token, userData);
                    postLoginNavigate(userData);
                } else {
                    throw new Error('Token não recebido.');
                }
            } catch (err: any) {
                setStatus('error');
                setMessage(err.message || 'Erro ao processar solicitação com Discord.');
            }
        };

        processLogin();
    }, [searchParams, navigate, authLogin]);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-950 px-4">
            <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl">
                {status === 'loading' && (
                    <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
                        <Loader2 className="h-12 w-12 text-orange-500 animate-spin mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">Aguarde</h2>
                        <p className="text-gray-400">{message}</p>
                    </div>
                )}
                {status === 'success' && (
                    <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
                        <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">Sucesso!</h2>
                        <p className="text-gray-300">{message}</p>
                    </div>
                )}
                {status === 'error' && (
                    <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
                        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">Erro no Login</h2>
                        <p className="text-red-400 mb-6">{message}</p>
                        <button onClick={() => navigate('/entrar')} className="px-6 py-2 bg-neutral-800 hover:bg-neutral-700 text-white rounded-lg transition-colors font-medium border border-neutral-700">Voltar para Login</button>
                    </div>
                )}
            </div>
        </div>
    );
}
